// Fuzzball Quiz Extras Manager
// Manages extras selections across popup and summary view

window.QuizExtrasManager = {
  selectedExtras: {
    cat1: [],
    cat2: [],
    cat3: [],
    cat4: []
  },
  
  init() {
    // Try to load from unified step10Selections first
    const step10Data = JSON.parse(localStorage.getItem('step10Selections') || '{}');
    if (step10Data.extras) {
      this.selectedExtras = step10Data.extras;
    } else {
      // Fall back to old selectedExtras key
      const saved = localStorage.getItem('selectedExtras');
      if (saved) {
        try {
          const parsed = JSON.parse(saved);
          // Handle migration from old format (array) to new format (object with cats)
          if (Array.isArray(parsed)) {
            // Old format - migrate to cat1 
            this.selectedExtras = {
              cat1: parsed,
              cat2: [],
              cat3: [],
              cat4: []
            };
            this.save();
          } else {
            // New format - ensure all cat arrays exist
            this.selectedExtras = {
              cat1: parsed.cat1 || [],
              cat2: parsed.cat2 || [],
              cat3: parsed.cat3 || [],
              cat4: parsed.cat4 || []
            };
          }
        } catch (e) {
          this.selectedExtras = {
            cat1: [],
            cat2: [],
            cat3: [],
            cat4: []
          };
        } 
      }
    }
  },
  
  addExtra(productData, catNumber = 1) {
    const catKey = `cat${catNumber}`;
    console.log('📦 Adding extra:', {
      catNumber,
      catKey,
      productTitle: productData.title,
      currentExtras: this.selectedExtras
    });

    // Ensure cat array exists
    if (!this.selectedExtras[catKey]) {
      this.selectedExtras[catKey] = [];
    }

    // Check if already selected for this cat (max 1 of each per cat)
    const existingIndex = this.selectedExtras[catKey].findIndex(e => e.id === productData.id);
    if (existingIndex !== -1) {
      return false; // Already added for this cat
    }

    // User-driven edit — mark this cat's extras as user-edited. Extras have
    // no quiz-driven auto-populate path today, so there is no gate to flip
    // (cf. bundle / trail), but the flag round-trips for symmetry with the
    // other surfaces and clears alongside them on resetQuiz / clearQuizData.
    if (window.QuizState && typeof window.QuizState.markSelectionUserEdited === 'function') {
      window.QuizState.markSelectionUserEdited('extras', catKey);
    }
    
    // Check inventory limit and show toast if low stock
    if (productData.inventoryQuantity && productData.inventoryQuantity > 0 && productData.inventoryQuantity <= 5) {
      window.QuizUtils.Error.show(`${productData.title} - Only ${productData.inventoryQuantity} in stock`);
    }
    
    // Add to selections for this cat
    this.selectedExtras[catKey].push({
      id: productData.id,
      variantId: productData.variantId,
      title: productData.title,
      price: productData.price,
      quantity: 1,
      type: 'extra'
    });
    
    this.save();
    this.updateUI(catNumber);
    this.updateCheckoutTotal();
    return true;
  },
  
  removeExtra(productId, catNumber = 1) {
    const catKey = `cat${catNumber}`;

    if (!this.selectedExtras[catKey]) {
      return false;
    }

    const index = this.selectedExtras[catKey].findIndex(e => e.id === productId);
    if (index !== -1) {
      this.selectedExtras[catKey].splice(index, 1);
      // User-driven removal also counts as a manual edit.
      if (window.QuizState && typeof window.QuizState.markSelectionUserEdited === 'function') {
        window.QuizState.markSelectionUserEdited('extras', catKey);
      }
      this.save();
      this.updateUI(catNumber);
      this.updateCheckoutTotal();
      return true;
    }
    return false;
  },
  
  toggleExtra(productData, catNumber = 1) {
    const catKey = `cat${catNumber}`;
    
    if (!this.selectedExtras[catKey]) {
      this.selectedExtras[catKey] = [];
    }
    
    const existing = this.selectedExtras[catKey].find(e => e.id === productData.id);
    if (existing) {
      this.removeExtra(productData.id, catNumber);
      return false; // Now removed
    } else {
      return this.addExtra(productData, catNumber); // Returns true if added
    }
  },
  
  getExtras(catNumber = null) {
    if (catNumber) {
      const catKey = `cat${catNumber}`;
      return this.selectedExtras[catKey] || [];
    }
    // Return all extras if no cat specified
    return this.selectedExtras;
  },
  
  getCount(catNumber = null) {
    if (catNumber) {
      const catKey = `cat${catNumber}`;
      return (this.selectedExtras[catKey] || []).length;
    }
    // Return total count across all cats
    return Object.values(this.selectedExtras).reduce((total, catExtras) => total + catExtras.length, 0);
  },
  
  getTotalPrice(catNumber = null) {
    if (catNumber) {
      const catKey = `cat${catNumber}`;
      return (this.selectedExtras[catKey] || []).reduce((total, extra) => total + (extra.price || 0), 0);
    }
    // Return total price across all cats
    return Object.values(this.selectedExtras).reduce((total, catExtras) => {
      return total + catExtras.reduce((catTotal, extra) => catTotal + (extra.price || 0), 0);
    }, 0);
  },
  
  save() {
    // Save to both old key (for compatibility) and new unified key
    localStorage.setItem('selectedExtras', JSON.stringify(this.selectedExtras));
    
    // Also save to unified step10Selections
    const step10Data = JSON.parse(localStorage.getItem('step10Selections') || '{}');
    step10Data.extras = this.selectedExtras;
    step10Data.timestamp = Date.now();
    localStorage.setItem('step10Selections', JSON.stringify(step10Data));
  },
  
  clear(catNumber = null) {
    if (catNumber) {
      const catKey = `cat${catNumber}`;
      this.selectedExtras[catKey] = [];
    } else {
      // Clear all cats
      this.selectedExtras = {
        cat1: [],
        cat2: [],
        cat3: [],
        cat4: []
      };
    }
    this.save();
    this.updateUI(catNumber);
    this.updateCheckoutTotal();
  },
  
  updateUI(catNumber = null) {
    // Update extras summary in step 7
    this.updateExtrasSummary(catNumber);
    
    // Update extras popup buttons if open
    this.updateExtrasPopupButtons();
  },
  
  updateExtrasSummary(catNumber = null) {
    if (catNumber) {
      // Update the inline extras summary inside the trail-box-section
      let section = document.querySelector(`.trail-box-section[data-cat="${catNumber}"] .extras-inline-summary`);

      // For single cat mode, check for section without data-cat attribute
      if (!section && catNumber === 1) {
        section = document.querySelector('.trail-box-section:not([data-cat]) .extras-inline-summary');
      }

      const count = this.getCount(catNumber);
      const total = this.getTotalPrice(catNumber);

      if (section) {
        const countEl = section.querySelector('.extras-count');
        const priceEl = section.querySelector('.extras-total-price');

        if (countEl) countEl.textContent = count;
        if (priceEl) priceEl.textContent = total > 0 ? ` · £${total.toFixed(2)}` : '';

        // Show/hide based on whether there are extras
        section.style.display = count > 0 ? '' : 'none';
      }

      // Dispatch event for unified products manager
      const catExtras = this.getExtras(catNumber);
      document.dispatchEvent(new CustomEvent('extras-updated', {
        detail: {
          catNumber: catNumber,
          extras: catExtras
        }
      }));
    } else {
      // Update all cats
      const catCount = window.QuizState?.get('submissionData.catCount') || 1;
      if (catCount === 1) {
        this.updateExtrasSummary(1);
      } else {
        for (let i = 1; i <= catCount; i++) {
          this.updateExtrasSummary(i);
        }
      }
    }
  },
  
  updateExtrasPopupButtons(container = null) {
    const target = container || document.querySelector('.extras-popup-system');
    if (!target) return;

    // Multi-cat stacked sections: each .extras-cat-section has its own data-cat
    const catSections = target.querySelectorAll('.extras-cat-section[data-cat]');
    if (catSections.length > 0) {
      catSections.forEach(section => {
        const sectionCat = parseInt(section.getAttribute('data-cat')) || 1;
        this._updateCardsForCat(section, sectionCat);
      });
      return;
    }

    // Single cat / trail box: read cat from modal attribute
    const modal = target.closest('.trail-box-system') || target.closest('.extras-popup-system') || target;
    const editingCat = parseInt(modal.getAttribute('data-editing-cat')) || 1;
    this._updateCardsForCat(target, editingCat);
  },

  _updateCardsForCat(container, catNumber) {
    const allCards = container.querySelectorAll('.extras-product-card');
    allCards.forEach(card => {
      const btn = card.querySelector('.extras-add-btn');
      const inventoryQty = parseInt(card.dataset.inventoryQuantity || '0');
      const inventoryPolicy = card.dataset.inventoryPolicy || 'deny';
      const productImage = card.querySelector('.product-image');
      const inventoryStatus = card.querySelector('.product-inventory-status');

      // Check if out of stock
      if (inventoryQty <= 0 && inventoryPolicy !== 'continue') {
        btn.disabled = true;
        btn.textContent = 'Out of stock';
        btn.classList.add('out-of-stock');
        if (productImage) productImage.style.opacity = '0.5';
        if (inventoryStatus) inventoryStatus.style.display = 'block';
      } else {
        btn.disabled = false;
        btn.classList.remove('out-of-stock');
        if (productImage) productImage.style.opacity = '1';
        if (inventoryStatus) inventoryStatus.style.display = 'none';

        // Check if already selected for current cat
        const catExtras = this.getExtras(catNumber);
        const isSelected = catExtras.find(e => e.id === card.dataset.productId);
        if (isSelected) {
          btn.innerHTML = 'Remove';
          btn.classList.add('added');
        } else {
          btn.innerHTML = 'Add<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17 9H3L2 21H10M14 11V7C14 4.79086 12.2091 3 10 3C7.79086 3 6 4.79086 6 7V11" stroke="#434548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M17 13V23M22 18L12 18" stroke="#434548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
          btn.classList.remove('added');
        }
      }
    });
  },

  updateTrailBoxExtrasButtons() {
    const trailBoxExtras = document.querySelector('.trail-box-system .trail-box-extras-section');
    if (!trailBoxExtras) return;
    this.updateExtrasPopupButtons(trailBoxExtras);
  },
  
  updateCheckoutTotal() {
    // Update the trial tab price display (TRIAL_BOX_PRICE + extras)
    const trialTabContent = document.querySelector('.tab-content[data-tab-content="trial"]');
    if (trialTabContent) {
      const trialPlanPrice = trialTabContent.querySelector('.plan-pricing .plan-price');
      if (trialPlanPrice) {
        // Get base trial price (TRIAL_BOX_PRICE × number of cats)
        const submissionData = window.QuizState.get('submissionData') || {};
        const numberOfCats = submissionData.catCount || 1;
        let totalTrialPrice = window.QuizConstants.FOOD.TRIAL_BOX_PRICE * numberOfCats;
        
        // Add extras total
        const extrasTotal = this.getTotalPrice();
        totalTrialPrice += extrasTotal;
        
        // Update the displayed price
        trialPlanPrice.textContent = `£${totalTrialPrice.toFixed(2)}`;
      }
    }
    
    // Update the trial checkout total when extras change
    const activeTab = document.querySelector('.plan-tabs .tab.active');
    if (activeTab && activeTab.dataset.tab === 'trial') {
      // Only update if we're on the trial tab
      if (window.fuzzballQuiz && window.fuzzballQuiz.updateCheckoutBarForTab) {
        window.fuzzballQuiz.updateCheckoutBarForTab('trial');
      }
    }

    // Update plan summary when extras change
    if (window.fuzzballQuiz && window.fuzzballQuiz.lastCalculatedRecommendations) {
      window.fuzzballQuiz.updatePlanSummary(window.fuzzballQuiz.lastCalculatedRecommendations);
    }
  }
};

// Initialize on load
window.QuizExtrasManager.init();